import { useMutation, useQueryClient } from '@tanstack/react-query';
import useAppToast from '@/hooks/useAppToast';
import { retrySyncRecord } from '@/services/admin/sync-notifications';

/**
 * Per-row 再同期 — re-push ONE sync record by aggregate_id.
 * (Bulk "全て再同期" uses useRetrySyncMutation instead.)
 */
export const useRetrySyncRecordMutation = () => {
  const toast = useAppToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      siteType,
      aggregate,
      aggregateId,
    }: {
      siteType: string;
      aggregate: string;
      aggregateId: string;
    }) => retrySyncRecord(siteType, aggregate, aggregateId),
    onSuccess: async () => {
      toast.success('同期が完了しました。');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sync-notifications'] }),
        queryClient.invalidateQueries({ queryKey: ['sync-notifications-count'] }),
      ]);
    },
    onError: error => {
      const status = (error as any)?.response?.status;
      if (status === 401) return;

      // BD 24.0 — the record came good while the list was on screen. The server sends its own sentence and a
      // 409, because nothing happened: swallowing it into the generic error would tell the operator something
      // broke, when in fact the work was already done.
      if (status === 409) {
        toast.error(
          (error as any)?.response?.data?.message ??
            'このレセプトは既に同期済みのため、再同期できません。'
        );
        // Refetch on this path too: a 409 means the screen is out of date, and leaving the stale row there
        // with its 再同期 button invites the same press again.
        void queryClient.invalidateQueries({ queryKey: ['sync-notifications'] });
        void queryClient.invalidateQueries({ queryKey: ['sync-notifications-count'] });
        return;
      }

      toast.error('エラーが発生しました。');
    },
  });
};
